'use strict';

// Pure programme selection shared by the staff browser and the monitor backend.
// Keep this module free of server imports and side effects.
// Programme sequence position is deliberately independent of completed workflow steps.
const DAY_MS = 86400000;
const text = value => String(value == null ? '' : value).trim();
const stepName = step => text(step && step.name).toLowerCase();
const positive = value => Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : null;

function dateMs(value) {
  if (value == null || value === '') return null;
  if (value && typeof value.toDate === 'function') {
    try { return dateMs(value.toDate()); } catch { return null; }
  }
  if (value && typeof value === 'object' && !(value instanceof Date)) {
    const seconds = value.seconds ?? value._seconds;
    if (Number.isFinite(Number(seconds))) return Number(seconds) * 1000;
  }
  const parsed = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
}

function comparable(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  const stamp = dateMs(value);
  return stamp == null ? '' : new Date(stamp).toISOString();
}

function normalizeSteps(steps) {
  if (Array.isArray(steps)) return steps;
  if (steps && typeof steps === 'object') {
    return Object.keys(steps).sort((a, b) => Number(a) - Number(b)).map(key => steps[key]);
  }
  return [];
}

function isStepDone(step) {
  const consultationDone = /consulenza/.test(stepName(step)) &&
    /^(fatt[ao]|rifiutat[ao]|scalat[ao])$/.test(text(step.consulenzaStatus).toLowerCase());
  return consultationDone || !!(step && step.done);
}

function initialFormStatus(programme) {
  const initialSteps = normalizeSteps(programme && programme.steps).filter(step =>
    /questionario/.test(stepName(step)) ||
    (/check|scadenza/.test(stepName(step)) && step.convertedFromQuestionario === true));
  if (!initialSteps.length) return 'unknown';
  if (initialSteps.some(step => !!step.done)) return 'submitted';
  const hasSavedForm = initialSteps.some(step => {
    const metadata = step.questionnaire || {};
    return [step.questionarioDocId, step.questionnaireDocId, metadata.id,
      metadata.questionarioUid, metadata.questionarioDocId, metadata.questionnaireDocId]
      .some(value => typeof value === 'string' && value.trim());
  });
  return hasSavedForm ? 'draft' : 'not_started';
}

function isProgrammeComplete(programme) {
  const steps = normalizeSteps(programme && programme.steps);
  if (!steps.length) return false;
  if (steps.some(step => /check|scadenza/.test(stepName(step)) &&
    !step.convertedFromQuestionario && isStepDone(step))) return true;
  return steps.filter(step => !/feedback/.test(stepName(step))).every(isStepDone);
}

const completeForLocking = programme => !!programme.historyLocked || isProgrammeComplete(programme);

function expiryMs(programme) {
  const delivery = programme.steps.find(step => /consegna/.test(stepName(step)));
  const anchor = dateMs(delivery && (delivery.doneAt || delivery.plannedAt));
  if (anchor != null) return anchor + 30 * DAY_MS;
  const stored = dateMs(programme.expireAt);
  if (stored != null) return stored;
  const start = dateMs(programme.plannedStartAt || programme.createdAt);
  const months = positive(programme.duration_months);
  if (start == null || months == null) return null;
  const expiry = new Date(start);
  // Match the monitor backend's UTC calendar even across browser DST changes.
  expiry.setUTCMonth(expiry.getUTCMonth() + months);
  return expiry.getTime();
}

function meaningfulLabel(value) {
  const label = text(value).split(/\s*(?:->|→)\s*/).pop();
  return /^(?:programma|percorso)(?: di allenamento)?(?:\s*\d+\s*\/\s*\d+)?$/i.test(label) ? '' : label;
}

function classifyProgramme(programme, products = []) {
  const catalog = products instanceof Map ? products : new Map(products.map(product => [text(product.id), product]));
  const product = catalog.get(text(programme.productId));
  // A transformed productName is the current product. An old catalogue SKU must not win.
  const productLabel = meaningfulLabel(programme.productName) || meaningfulLabel(programme.title) ||
    meaningfulLabel(product && product.name) || text(product && product.sku) || 'Programma non classificato';
  const normalized = productLabel.toLowerCase();
  const compact = normalized.replace(/[^a-z0-9]/g, '');
  const top = /\btop\b/.test(normalized) || /(?:fc\d+|protocollo\d+|prot\d+)top|\d+topsoloallenamento/.test(compact);
  const tier = top ? 'TOP' : /plus/.test(normalized) ? 'PLUS' : /basic/.test(normalized) ? 'BASIC' : 'Altri';
  const service = /solo\s*allenamento/.test(normalized) ? 'solo allenamento' :
    /solo\s*(?:dieta|alimentazione)/.test(normalized) ? 'solo alimentazione' : 'completo';
  return { productLabel, tier, service };
}

function userHold(user) {
  const hold = user && user.operationalHold;
  return hold === true || !!(hold && (hold.active === true || hold.status === 'active' || hold.operationalHoldActive === true)) ||
    !!(user && user.operationalHoldActive === true);
}

function sequenceSort(a, b) {
  return (Number(a.sequenceIndex) || 0) - (Number(b.sequenceIndex) || 0) ||
    comparable(a.plannedStartAt || a.createdAt).localeCompare(comparable(b.plannedStartAt || b.createdAt));
}

const isPtOnly = classification => /(?:sedut[ae]|session[ei])\s*(?:pt|personal\s*train)|\bpersonal\s*training\b/i
  .test(classification.productLabel);

function selectCurrentProgrammes({ percorsi = [], users = [], products = [], now = new Date() } = {}) {
  const nowMs = dateMs(now);
  if (nowMs == null) throw new Error('Data del monitor non valida');
  const usersById = new Map(users.map(user => [text(user.id || user.uid), user]));
  const productsById = new Map(products.map(product => [text(product.id), product]));
  const byUid = new Map();
  const anomalies = { withoutUid: 0, missingProfileUsers: 0, invalidFractions: 0, multipleCurrentUsers: 0, excludedPtProgrammes: 0 };
  for (const raw of percorsi) {
    if (!raw || !raw.id || raw.consulenzeOnly) continue;
    const uid = text(raw.uid || raw.userUid);
    if (!uid) { anomalies.withoutUid++; continue; }
    const list = byUid.get(uid) || [];
    list.push({ ...raw, uid, steps: normalizeSteps(raw.steps) });
    byUid.set(uid, list);
  }
  const records = [];
  const programmeGroups = [];
  for (const [uid, programmes] of byUid) {
    const user = usersById.get(uid);
    if (!user) { anomalies.missingProfileUsers++; continue; }
    const grouped = new Map();
    for (const programme of programmes) {
      const id = text(programme.sequenceGroupId || programme.id);
      const rows = grouped.get(id) || [];
      rows.push(programme);
      grouped.set(id, rows);
    }
    const groups = [...grouped].map(([id, rows]) => {
      rows.sort(sequenceSort);
      const first = rows[0];
      const anchors = rows.map(row => comparable(row.orderSequenceAnchor || row.plannedStartAt || row.createdAt)).filter(Boolean);
      return {
        uid, id, rows, orderRef: text(first.orderRef), orderSource: text(first.orderSource),
        anchor: anchors.sort()[0] || '',
        priority: Number.isFinite(Number(first.orderSequencePriority)) ? Number(first.orderSequencePriority) : Number.MAX_SAFE_INTEGER,
        completed: rows.every(completeForLocking),
        productLabel: classifyProgramme(first, productsById).productLabel,
      };
    }).sort((a, b) => a.anchor.localeCompare(b.anchor) ||
      ((a.orderRef && a.orderRef === b.orderRef) ? a.priority - b.priority : 0) ||
      a.orderRef.localeCompare(b.orderRef) || a.id.localeCompare(b.id));
    let blockFollowing = false;
    let currentCount = 0;
    groups.forEach((group, order) => {
      group.order = order;
      programmeGroups.push(group);
      const gating = group.rows.length > 1 || Number(group.rows[0].sequenceTotal) > 1;
      let blockNext = false;
      let current = null;
      for (const programme of group.rows) {
        const override = programme.sequenceUnlockOverride === true;
        const complete = isProgrammeComplete(programme);
        const locked = !!programme.historyLocked || (complete && programme.completedUnlockOverride !== true) ||
          (blockFollowing && !override) || (gating && blockNext && !override);
        if (!current && !locked && !complete) current = programme;
        if (gating) blockNext = !completeForLocking(programme);
      }
      if (current) {
        const classification = classifyProgramme(current, productsById);
        if (isPtOnly(classification)) {
          anomalies.excludedPtProgrammes++;
          if (!group.rows.every(row => row.sequenceUnlockOverride === true || completeForLocking(row))) blockFollowing = true;
          return;
        }
        currentCount++;
        const index = positive(current.sequenceIndex);
        const total = positive(current.sequenceTotal);
        const validFraction = index != null && total != null && Number.isInteger(index) && Number.isInteger(total) && index <= total;
        if (!validFraction) anomalies.invalidFractions++;
        const earlierComplete = group.rows.some(row => Number(row.sequenceIndex) < index && completeForLocking(row));
        const everDelivered = group.rows.some(row => row.steps.some(step => /consegna/.test(stepName(step)) && isStepDone(step)));
        const meaningfulProgress = current.steps.some(step => isStepDone(step) && !/consulenza/.test(stepName(step)));
        const started = index > 1 || earlierComplete || everDelivered || meaningfulProgress;
        const held = userHold(user);
        const expires = expiryMs(current);
        const name = text(user.name || user.nome || user.firstName || user.first_name);
        const surname = text(user.surname || user.cognome || user.lastName || user.last_name);
        const previousCompleted = groups.slice(0, order).some(previous => previous.completed);
        records.push({
          uid, programmeId: current.id, groupId: group.id, groupOrder: order, groupAnchor: group.anchor,
          orderRef: text(current.orderRef), orderSource: text(current.orderSource),
          name, surname, userName: [name, surname].filter(Boolean).join(' ') || text(user.email || user.userEmail) || uid,
          email: text(user.email || user.userEmail),
          phone: text(user.phone || user.telefono || user.phoneNumber),
          index, total, fraction: validFraction ? `${index}/${total}` : 'Da verificare', validFraction,
          ...classification,
          initialFormStatus: initialFormStatus(group.rows[0]),
          started, held, state: held ? 'hold' : started ? 'started' : 'waiting',
          delivered: current.steps.some(step => /consegna/.test(stepName(step)) && isStepDone(step)),
          expired: expires != null && expires < nowMs,
          expiresAt: expires == null ? null : new Date(expires).toISOString(),
          expireAt: expires == null ? null : new Date(expires).toISOString(),
          plannedStartAt: comparable(current.plannedStartAt) || null,
          startAt: comparable(current.plannedStartAt) || null,
          completedProgrammes: group.rows.filter(completeForLocking).length,
          previousCompletedGroup: previousCompleted, previousCompleted,
        });
      }
      if (!group.rows.every(row => row.sequenceUnlockOverride === true || completeForLocking(row))) blockFollowing = true;
    });
    if (currentCount > 1) anomalies.multipleCurrentUsers++;
  }
  return { records, programmeGroups, anomalies };
}

function selectCompletedProgrammes({ records = [], programmeGroups = [], users = [], products = [] } = {}) {
  const currentUids = new Set(records.map(record => record.uid));
  const usersById = new Map(users.map(user => [text(user.id || user.uid), user]));
  const productsById = new Map(products.map(product => [text(product.id), product]));
  const latestGroups = new Map();
  const groupsByUid = new Map();
  for (const group of programmeGroups) {
    const groups = groupsByUid.get(group.uid) || [];
    groups.push(group);
    groupsByUid.set(group.uid, groups);
    const terminal = group.rows[group.rows.length - 1];
    if (!terminal || isPtOnly(classifyProgramme(terminal, productsById))) continue;
    const previous = latestGroups.get(group.uid);
    if (!previous || group.order > previous.order) latestGroups.set(group.uid, group);
  }
  const completedRecords = [];
  for (const [uid, group] of latestGroups) {
    const user = usersById.get(uid);
    if (!user || currentUids.has(uid)) continue;
    const terminal = group.rows[group.rows.length - 1];
    const total = positive(terminal.sequenceTotal);
    // A partial/duplicated sequence and a manual historical lock cannot prove
    // that a whole course reached its real final check.
    if (!Number.isInteger(total) || group.rows.length !== total || terminal.historyLocked ||
      !group.rows.every((row, index) => Number(row.sequenceIndex) === index + 1 && Number(row.sequenceTotal) === total) ||
      !group.rows.slice(0, -1).every(completeForLocking)) continue;
    const finalChecks = terminal.steps.filter(step => /check|scadenza/.test(stepName(step)) &&
      !step.convertedFromQuestionario && !!step.done);
    if (!finalChecks.length) continue;
    const completedDates = finalChecks.map(step => dateMs(step.doneAt)).filter(value => value != null);
    const completedAt = completedDates.length ? new Date(Math.max(...completedDates)).toISOString() : null;
    const expires = expiryMs(terminal);
    const name = text(user.name || user.nome || user.firstName || user.first_name);
    const surname = text(user.surname || user.cognome || user.lastName || user.last_name);
    const previousCompleted = groupsByUid.get(uid).some(previous => previous.order < group.order && previous.completed);
    completedRecords.push({
      uid, programmeId: terminal.id, groupId: group.id, groupOrder: group.order, groupAnchor: group.anchor,
      orderRef: text(terminal.orderRef), orderSource: text(terminal.orderSource),
      name, surname, userName: [name, surname].filter(Boolean).join(' ') || text(user.email || user.userEmail) || uid,
      email: text(user.email || user.userEmail), phone: text(user.phone || user.telefono || user.phoneNumber),
      index: total, total, fraction: `${total}/${total}`, validFraction: true,
      ...classifyProgramme(terminal, productsById),
      initialFormStatus: initialFormStatus(group.rows[0]),
      started: true, held: userHold(user), state: 'completed', completedAt,
      delivered: terminal.steps.some(step => /consegna/.test(stepName(step)) && isStepDone(step)),
      expired: false,
      expiresAt: expires == null ? null : new Date(expires).toISOString(),
      expireAt: expires == null ? null : new Date(expires).toISOString(),
      plannedStartAt: comparable(terminal.plannedStartAt) || null,
      startAt: comparable(terminal.plannedStartAt) || null,
      completedProgrammes: total, previousCompletedGroup: previousCompleted, previousCompleted,
    });
  }
  return completedRecords;
}

module.exports = {
  selectCurrentProgrammes, selectCompletedProgrammes, classifyProgramme,
  isProgrammeComplete, dateMs, normalizeSteps, isStepDone, initialFormStatus, completeForLocking,
};
